<template>
  <div>
    <div class="editor">
      <div class="container">
        <Menu
          v-if="editor && engine"
          v-bind:engine="engine"
          v-bind:editor="editor"
          v-bind:currentModule="currentModule"
          v-bind:modules="modules"
          v-bind:activeModule="activeModule"
          v-bind:selectedPeriod="selectedPeriod"
          @selectedPeriodChange="selectedPeriodChange"
          @update-active-module="updateActiveModule"
        />
        <div id="rete" ref="rete" class="node-editor"></div>
        <ModuleSelector
          ref="moduleSelector"
          v-if="editor && engine && modules"
          v-bind:engine="engine"
          v-bind:editor="editor"
          v-bind:currentModule="currentModule"
          v-bind:modules="modules"
          v-bind:activeModule="activeModule"
          @update-active-module="updateActiveModule"
        />
      </div>
    </div>
    <Terms
      v-if="isTermsShown"
      v-bind:editor="editor"
    />
    <PopUp
      v-if="popupData"
      v-bind:data="popupData"
      v-bind:editor="editor"
      v-bind:modules="modules"
      v-bind:selectedPeriod="selectedPeriod"
      @update-popup-data="updatePopUpData"
    />
    <HelpPopUp 
      v-if="isHelpPopUpShown"
    />
    <TabView 
      v-if="isTabViewPopUpShown"
      v-bind:tabViewData="tabViewData"
    />
    <Modal 
      v-if="isModalShown"
      v-bind:modalData="modalData"
    />
    <ParameterModal 
      v-if="isParameterModalShown"
      v-bind:parameterModalData="parameterModalData"
    />
  </div>
</template>

<script>
  import Vue from 'vue';

  import Menu from '../Menu/Menu.vue';
  import ModuleSelector from '../ModuleSelector/ModuleSelector.vue';
  import PopUp from '../PopUp/PopUp.vue';
  import HelpPopUp from '../Help/HelpPopUp.vue';
  import Terms from '../Terms/Terms.vue';
  import TabView from '../TabView/TabView.vue';
  import Modal from '../Modal/Modal.vue';
  import ParameterModal from '../ParameterModal/ParameterModal.vue';
  import Rete from 'rete';
  import ConnectionPlugin from 'rete-connection-plugin';
  import VueRenderPlugin from 'rete-vue-render-plugin';
  import ContextMenuPlugin from 'rete-context-menu-plugin';
  import AreaPlugin from 'rete-area-plugin';
  import CommentPlugin from '../../util/reteCustomPlugins/grouping';
  import KeyboardPlugin from '../../util/reteCustomPlugins/keyboard';
  import ExportPlugin from '../../util/reteCustomPlugins/export/export';
  import LifecyclePlugin from 'rete-lifecycle-plugin';

  import { copy, paste } from '../../util/reteCustomPlugins/keyboard';
  import HistoryPlugin from 'rete-history-plugin';
  import ModulePlugin from '../../util/reteCustomPlugins/module';

  import { MAIN_MODULE_NAME } from '../../util/constants/index'

  import compile from '../../util/compile';
  import openModule from '../../util/openModule';

  import {
    InputComponent,
    ModuleComponent,
    OutputComponent,
    NumComponent,
    SquareDistComponent,
    TriangleDistComponent,
    NormDistComponent,
    ExpDistComponent,
    AddComponent,
    ResultComponent,
    SubtractComponent,
    MultiplyComponent,
    DivideComponent,
    PowerComponent,
    IfNullComponent,
    SwitchComponent,
    MixerComponent
  } from '../../util/reteExtensions';
  
  import CustomNodeComponent from '../../templates/CustomNodeComponent.vue';

  export default {
    name: 'Editor',
    components: {
      Menu,
      ModuleSelector,
      PopUp,
      HelpPopUp,
      Modal,
      ParameterModal,
      TabView,
      Terms,
    },
    props: {
      orderedObject: Array,
    },
    data () {
      return {
        editor: undefined,
        engine: undefined,
        currentModule: {},
        modules: undefined,
        activeModule: MAIN_MODULE_NAME,
        selectedPeriod: Vue.prototype.$globalSharedData.selectedPeriod,
        popupData: undefined,
        isHelpPopUpShown: false,
        isTermsShown: false, // must be true initially if we want to show it.
        isModalShown: false,
        isTabViewPopUpShown: false,
        modalData: undefined,
        isParameterModalShown: false,
        parameterModalData: undefined,
        tabViewData: undefined,
      }
    },
    methods: {
      selectedPeriodChange(value) {
        this.selectedPeriod = value;
        this.$emit("selectedPeriodChange", value);
      },
      updateActiveModule(value) {
        this.activeModule = value;
      },
      updateCommentForNodeByID(arrayOfNodeIDAndComment) {
        // value is an array of two elements: the node's id and the comment as a string

        let nodeId = arrayOfNodeIDAndComment[0];
        let comment = arrayOfNodeIDAndComment[1];

        // Find the node within this.editor.nodes; maybe like this: let nodeSearch = this.editor.nodes.find(n => n.id == node.id);
        // Update it's comment value
        // console.log('updateCommentForNodeByID', arrayOfNodeIDAndComment);
        // console.log(this.editor.nodes)

        for (let i = 0; i < this.editor.nodes.length; i++){
          if (this.editor.nodes[i].id == nodeId){
            this.editor.nodes[i].data.comment = comment;
          }
        }

        // console.log(this.editor.nodes);

      },
      returnCommentForNodeID(value){

        let comment = '';

        if (typeof value != "undefined"){
          for (let i = 0; i < this.editor.nodes.length; i++){
            if (this.editor.nodes[i].id == value){
              if (this.editor.nodes[i].data.comment != "undefined"){
                comment = this.editor.nodes[i].data.comment;
              }
            }
          }
        }

        Vue.prototype.$eventHub.$emit("receiveCommentForNodeIDEvent", comment);

      },
      showHelpPopUp(value) {
        this.isHelpPopUpShown = value;
      },
      showTermsPopUp(value) {
        this.isTermsShown = value;
      },
      showTabViewPopUp(data) {

        if (data.showTabView){
          data.tabViewData['activeModule'] = this.activeModule;
          this.tabViewData = data.tabViewData;
          // console.log(this.tabViewData);

          // Show popup of Tab View
          this.isTabViewPopUpShown = true;

        } else {
          this.isTabViewPopUpShown = false;
          this.tabViewData = undefined;
        }


      },
      showModal(modalConfig) {
        
        if (modalConfig.isModalShown){
          this.modalData = modalConfig.modalData;
          this.isModalShown = true;
        } else {
          this.isModalShown = false;
          this.modalData = undefined;
        }
        
      },
      showParameterModal(modalConfig) {
        
        if (modalConfig.isParameterModalShown){
          this.parameterModalData = modalConfig.parameterModalData;
          this.isParameterModalShown = true;
        } else {
          this.isParameterModalShown = false;
          this.parameterModalData = undefined;
        }
        
      },
      updatePopUpData(value) {
        // console.log('editor updatePopupData')
        this.popupData = value ? {...value} : false;

        // Add activeModule to the data
        // console.log('this.activeModule', this.activeModule)
        // console.log('this.popupData', this.popupData)
        if (this.popupData){
          this.popupData['activeModule'] = this.activeModule;
        }
        

      },
    },
    mounted () {
      let reteContainer = document.querySelector('#rete');
      let engine = new Rete.Engine('spurs@0.1.0');
      let editor = new Rete.NodeEditor('spurs@0.1.0', reteContainer);

      // Event listeners
      this.$eventHub.$on('plotData', this.updatePopUpData);               // event listener for changes in the distribution details popup
      this.$eventHub.$on('showHelpPopUpEvent', this.showHelpPopUp);       // event listener for help popup
      this.$eventHub.$on('updateCommentForNodeByIDEvent', this.updateCommentForNodeByID); // event listener for node comment updates
      this.$eventHub.$on('returnCommentForNodeIDEvent', this.returnCommentForNodeID); // event listener for comment requests for node id
      this.$eventHub.$on('showModal', this.showModal); // event listener for the modal
      this.$eventHub.$on('showParameterModal', this.showParameterModal); // event listener for the modal
      this.$eventHub.$on('showTabViewPopUpEvent', this.showTabViewPopUp); // event listener for the modal
      this.$eventHub.$on('showTermsEvent', this.showTermsPopUp); // event listener for the Terms and Conditions
         
      
      const components = [
        new NumComponent(),
        new SquareDistComponent(),
        new TriangleDistComponent(),
        new NormDistComponent(),
        new ExpDistComponent(),
        new AddComponent({editor, engine}),
        new ResultComponent(),
        new SubtractComponent(),
        new MultiplyComponent(),
        new DivideComponent(),
        new PowerComponent(),
        new IfNullComponent(),
        new SwitchComponent(),
        new MixerComponent(),
        new InputComponent(),
        new ModuleComponent(),
        new OutputComponent()
      ];

      let modules = { [MAIN_MODULE_NAME]: { data: {id: 'spurs@0.1.0', nodes: {}}}};
      let currentModule = {};

      

      /************************************************* 
        Declare which Rete Plugins we will use
      *************************************************/

      editor.use(LifecyclePlugin);
      editor.use(VueRenderPlugin, { component: CustomNodeComponent });
      editor.use(ConnectionPlugin);
      editor.use(ContextMenuPlugin, {
        searchBar: false,
        delay: 100,
        allocate(component) { return component.path },
        rename(component) { return component.contextMenuName || component.name },
        items: {
          'Comment'() {
            // let path = ["Comment"];
            const position = Object.values(editor.view.area.mouse);
            editor.trigger('addcomment', ({ type: 'inline', position }))
          },
          'Grouping'() {
            this.path = ['Comment'];
            const ids = editor.selected.list.map(node => node.id);
            const nodes = ids.map(id => editor.nodes.find(n => n.id === id));
            editor.trigger('addcomment', ({ type: 'frame', nodes }))
          },
        },
        nodeItems: node => {

          let menu = { }

          // Clone All functionality
          if (editor.selected.list.length > 1) {
            menu['Clone'] = false;
            menu['Clone All'] = () => {
              let copies = copy(editor);
              paste(editor, copies);
            };
          }

          // Change Title functionality
          menu['Change Title'] = () => {
            let nodeTitle = prompt("Please enter the node title:", node.data.title?node.data.title:node.name);

            if (nodeTitle == null || nodeTitle == "") {
              delete node.data.title;
            } else {
              node.data.title = nodeTitle;
            }
            node.update();
            // Recompile, so that the node's name is immediately reflected in the popup etc.
            compile({
              engine,
              editor,
            }); 
          };

          menu['Grouping'] = () => {
            const ids = editor.selected.list.map(node => node.id);
            const nodes = ids.map(id => editor.nodes.find(n => n.id === id));
            editor.trigger('addcomment', ({ type: 'frame', nodes }))
          };

          return menu;
        }
      });
      editor.on('nodeselected', (node) => {
        // console.log(node.id);
      });

      editor.use(AreaPlugin);
      editor.use(CommentPlugin, {
        disableBuiltInEdit: false,
        onUpdate: () => {
          Vue.set(this.modules, this.activeModule, {data: editor.toJSON()});

          editor.use(ModulePlugin, { engine, modules: this.modules });
          compile({
            engine,
            editor,
          }); 
        }
      });
      editor.use(KeyboardPlugin);
      editor.use(ExportPlugin);
      editor.use(HistoryPlugin, { keyboard: false });

      this.modules = modules;
      editor.use(ModulePlugin, { engine, modules: this.modules });

      components.map(c => {
        editor.register(c);
        engine.register(c);
      });

      /**************************************************************** 
        Event Listeners that lead to another compile action
       ***************************************************************/
       // Pascal removed: nodeselected // otherwise each click / select event will trigger recalculation
      editor.on('process nodecreated noderemoved connectioncreated connectionremoved commentcreated commentremoved syncframes', async () => {
        // this.modules[this.activeModule] = editor.toJSON();

        // This prevents an over-use of the compile calls (a module change then calls compile 3 times instead of >250 times)
        // It is not clear when exactly the editor.silent is true
        // See here for reference: https://github.com/retejs/rete/issues/221#issuecomment-547136265
        if(editor.silent) return;

        Vue.set(this.modules, this.activeModule, {data: editor.toJSON()});

        editor.use(ModulePlugin, { engine, modules: this.modules });

        compile({
          engine,
          editor,
        });

      });

      this.editor = editor;
      this.engine = engine;
      this.currentModule = currentModule;

      editor.view.resize();
      AreaPlugin.zoomAt(editor);
      editor.trigger('process');

      // to ensure the editor is on the right module at start
      openModule({
        name: MAIN_MODULE_NAME,
        engine,
        editor,
        currentModule, modules
      });
    }
  }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .editor {
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    height: 92vh;
    border-top: 1px solid #4B4B4B;
  }
  .container {
    flex: 1;
    overflow: hidden;
  }
  
</style>
